import React, { useEffect, useRef, useState } from "react";
import { Form, Button, Container, Row, Col, InputGroup } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import LayoutContainer from "../../components/Containers/LayoutContainer";
import PageHeader from "../../components/PageHeader/PageHeader";
import {
  ArrowLeftSquareFill,
  CaretDownFill,
  XCircleFill,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import "./AddQuiz.scss";
import CroppingModal from "../../components/Cropping Modal/CroppingModal";
import { Quiz } from "./QuizData";
import {
  postDetails,
  postMultipartDetails,
} from "../../services/http/http.service";
import APIList from "../../utils/constants/APIList";
import encrypt from "../../utils/helpers/encrypt";
import { validateImageFile } from "../../utils/helpers/validateFileUpload";
import { useAppDispatch } from "../../store/storeRequests";
import { hideToast, showToast } from "../../store/Toast/toast.slice";
import { hideLoader, showLoader } from "../../store/Loader/loader.slice";

const AddQuiz: React.FC = () => {
  const [data, setData] = useState<Quiz[]>([]);
  const [options, setOptions] = useState<string[]>([""]);
  const [isImageValid, setIsImageValid] = useState(true); // State to track image validity

  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [questionImagePreview, setQuestionImagePreview] = useState<
    string | null
  >(null);
  const [filePreviews, setFilePreviews] = useState<(string | null)[]>([null]);
  const [selectedFieldType, setSelectedFieldType] =
    useState<string>("Textual Option");
  const [showCropper, setShowCropper] = useState<boolean>(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(
    null
  );
  const [fileUploaded, setFileUploaded] = useState<boolean[]>(
    options.map(() => false)
  );
  const [optionErrors, setOptionErrors] = useState<string[]>([]);
  const [folderId, setFolderId] = useState<string>("");
  const [question, setQuestion] = useState("");
  const [questionFile, setQuestionFile] = useState<File | null>(null);
  const [optionFiles, setOptionFiles] = useState<File[]>([]);
  const [points, setPoints] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [optionFileErrors, setOptionFileErrors] = useState<string[]>([]);
  const [quizFolderType, setQuizFolderType] = useState('Quiz Folder');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const cmpMounted = useRef(false);

  useEffect(() => {
    if (cmpMounted.current === true) return;
    cmpMounted.current = true;
    fetchQuizData();
  }, []);

  const [folders, setFolders] = useState<
    { id: string; folder_name: string; isTrainingQuiz: 0 | 1 }[]
  >([]);

  const fetchQuizData = async () => {
    const now = new Date().setHours(0,0,0,0);
    try {
      dispatch(showLoader("Loading Data..."));
      const response = await postDetails(APIList.GET_ALL_QUIZ_FOLDER_NAME, {});
      setData(response.data);

      // const folderData = response.data.map((item: Quiz) => ({
      //   id: item.id,
      //   folder_name: item.folder_name,
      //   isTrainingQuiz: item.isTrainingQuiz,
      // }));
      const folderData = response.data.map((item: Quiz) => ({
        id: item.id,
        folder_name: item.folder_name,
        isTrainingQuiz: item.isTrainingQuiz,
        closeDate: item?.closeDate || null,
      }))
      .filter((item: Quiz) => {
        if (!item.closeDate) return true;
        return new Date(item.closeDate).setHours(0,0,0,0) >= now;
      })
      ;
      setFolders(folderData);

      if (folderData.length > 0) {
        handleQuizTypeChange(response.data[0].quiz_type);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleQuizTypeChange = (type: number) => {
    let fieldType: string;
    switch (type) {
      case 1:
        fieldType = "Textual Option";
        break;
      case 2:
        fieldType = "Descriptive Option";
        break;
      case 3:
        fieldType = "Image Option";
        break;
      case 4:
        fieldType = "Image Ordering Option";
        break;
      default:
        fieldType = "Textual Option";
        break;
    }
    setSelectedFieldType(fieldType);
    resetForm();
  };

  const handleAddOption = () => {
    setOptions([...options, ""]);
    setFilePreviews([...filePreviews, null]);
  };

  const resetForm = () => {
    setFilePreviews([]);
    setOptionFiles([]);
    setOptions([""]);
    setError(null);
  };

  const checkForDuplicates = (options: string[]): boolean => {
    for (let i = 0; i < options.length; i++) {
      for (let j = i + 1; j < options.length; j++) {
        if (options[i] === options[j]) {
          return true;
        }
      }
    }
    return false;
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptionErrors = [...optionErrors];
    const newOptions = [...options];
    newOptions[index] = value;
    const numericValue = value.replace(/\D/g, "");

    if (checkForDuplicates(newOptions)) {
      dispatch(showToast("Options must be unique."));
      setTimeout(() => dispatch(hideToast()), 3000);
    } else {
      setError(null);
    }
    if (selectedFieldType === "Textual Option" || numericValue.length <= 2) {
      setOptions(newOptions);
    }

    if (value.length === 0) {
      newOptionErrors[index] = "Please fill the option";
    } else {
      newOptionErrors[index] = "";
    }
    setOptionErrors(newOptionErrors);
  };

  const handleOptionSelect = (index: number) => {
    setSelectedOption(index);
    setShowWarning(true);
  };

  const handleQuizFolderTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setQuizFolderType(e.target.value);
    setFolderId(''); 
  };

  // const handleQuestionImageChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target.files?.[0];

  //   if (!file) return;

  //   const validFileTypes = ["image/jpeg", "image/jpg", "image/png"];
  //   if (!validFileTypes.includes(file.type)) {
  //     // setErrorMessage("Please upload a JPG/JPEG/PNG image.");
  //     dispatch(showToast("Please upload a JPG/JPEG/PNG image."));
  //     setTimeout(() => dispatch(hideToast()), 3000);
  //     return;
  //   }

  //   if (file) {
  //     const maxSizeInBytes = 11 * 1024 * 1024;
  //     if (file.size > maxSizeInBytes) {
  //       // setErrorMessage("The file size should not exceed 10MB.");
  //       dispatch(showToast("The file size should not exceed 10MB."))
  //       setTimeout(() => dispatch(hideToast()), 3000);
  //       return;
  //     }

  //     setQuestionFile(file);

  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setCurrentImage(reader.result as string);
  //       setShowCropper(true);
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // };
  const handleQuestionImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
  
    if (!file) return;
  
    // Validate the real extension + MIME type and reject deceptive
    // double-extension names (e.g. file.php.png, file.png.svg).
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setIsImageValid(false); // Mark image as invalid
      dispatch(showToast(validation.message));
      setTimeout(() => dispatch(hideToast()), 3000);
      return;
    } else {
      setIsImageValid(true); // Mark image as valid if it passes validation
    }
  
    // Check file size
    const maxSizeInBytes = 11000000000000 * 1024 * 1024; // 11MB
    if (file.size > maxSizeInBytes) {
      setIsImageValid(false); // Mark image as invalid
      // dispatch(showToast("The file size should not exceed 10MB."));
      // setTimeout(() => dispatch(hideToast()), 3000);
      return;
    }
  
    // If the file is valid
    setQuestionFile(file);
  
    const reader = new FileReader();
    reader.onloadend = () => {
      setCurrentImage(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };
  
  const handleImageChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      dispatch(showToast(validation.message));
      setTimeout(() => dispatch(hideToast()), 3000);
      return;
    }

    if (file) {
      // const maxSizeInBytes = 110000000 * 1024 * 1024;
      // if (file.size > maxSizeInBytes) {
      //   // setErrorMessage("The file size should not exceed 10MB.");
      //   dispatch(showToast("The file size should not exceed 10MB."));
      //   setTimeout(() => dispatch(hideToast()), 3000);
      //   return;
      // }

      setOptionFiles((prev) => {
        const newFiles = [...prev];
        newFiles[index] = file;
        return newFiles;
      });

      const currentOrder = options[index];

      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentImage(reader.result as string);
        setCurrentImageIndex(index);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);

      const newOptions = [...options];
      newOptions[index] = currentOrder;
      setOptions(newOptions);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageRemove = (index: number) => {
    const newPreviews = [...filePreviews];
    newPreviews[index] = null;
    setFilePreviews(newPreviews);
    const newOptions = [...options];
    newOptions[index] = "";
    setOptions(newOptions);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
    setFilePreviews(filePreviews.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setQuestion(e.target.value);

  const handleSelectedField = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSelectedFieldType(e.target.value);
    resetForm();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let hasError = false;
    const newOptionFileErrors = Array(options.length).fill("");

    if (
      selectedFieldType === "Image Option" ||
      selectedFieldType === "Image Ordering Option"
    ) {
      options.forEach((_, index) => {
        if (!optionFiles[index]) {
          newOptionFileErrors[index] = "Please upload a file.";
          hasError = true;
        }
      });
    }

    setOptionFileErrors(newOptionFileErrors);

    if (!isImageValid) {
      dispatch(showToast("Please upload a valid image file."));
      setTimeout(() => dispatch(hideToast()), 3000);
      return; // Prevent submission
    }
    if (hasError) {
      // setErrorMessage("Please ensure all required files are uploaded.");
      dispatch(showToast("Please ensure all required files are uploaded."));
      setTimeout(() => dispatch(hideToast()), 3000);
      return;
    }

    if (!folderId) {
      // setErrorMessage("Select a Folder.");
      dispatch(showToast("Select a Folder."));
      setTimeout(() => dispatch(hideToast()), 3000);
      return;
    }

    if (
      (selectedFieldType !== "Descriptive Option" && options.length < 2) ||
      options.length === 0 ||
      (selectedFieldType !== "Image Ordering Option" &&
        selectedOption === null) ||
      (selectedFieldType !== "Descriptive Option" &&
        checkForDuplicates(options))
    ) {
      let errorMessage = "";

      if (selectedFieldType !== "Descriptive Option" && options.length < 2) {
        errorMessage += "Please add at least two options.\n";
      }

      if (options.length === 0) {
        errorMessage += "Please add at least one option.\n";
      }

      if (
        selectedFieldType !== "Image Ordering Option" &&
        selectedFieldType !== "Descriptive Option" &&
        selectedOption === null
      ) {
        errorMessage += "Please select the correct answer.\n";
      }

      if (
        selectedFieldType !== "Descriptive Option" &&
        checkForDuplicates(options)
      ) {
        selectedFieldType !== "Descriptive Option" &&
        checkForDuplicates(options)
      }

      if (errorMessage) {
        // setErrorMessage(errorMessage.trim());
        dispatch(showToast(errorMessage.trim()));
      setTimeout(() => dispatch(hideToast()), 5000);
        return;
      }
    }

    const formData = new FormData();

    const formattedOptions = options.map((option, index) => {
      if (selectedFieldType === "Image Ordering Option") {
        return {
          text: optionFiles[index]?.name || "",
          value: option,
        };
      } else {
        return {
          text: option,
          value: selectedOption === index ? "true" : "false",
        };
      }
    });

    if (questionFile) {
      formData.append("question_file", questionFile);
    }

    options.forEach((_, index) => {
      if (optionFiles[index]) {
        formData.append(`option_file`, optionFiles[index]);
      }
    });

    const quizTypeMap: { [key: string]: number } = {
      "Textual Option": 1,
      "Descriptive Option": 2,
      "Image Option": 3,
      "Image Ordering Option": 4,
    };

    const quizType = quizTypeMap[selectedFieldType] || 1;

    const payload = {
      folder_id: Number(folderId),
      quiz_type: quizType,
      question,
      options: formattedOptions,
      point: points,
    };

    formData.append("data", encrypt(payload));
    console.log(payload);

    try {
      const response = await postMultipartDetails(APIList.ADD_QUIZ, formData);
      // console.log("Response:", response);
      if (response.statusCode === 200) {
        redirectToQuizFolder();
        dispatch(showToast("Quiz Added Successfully."));
      } else if(response.status === "Failed") {
        if(response.message === "You are not authorized to perform this action."){
        dispatch(showToast("Cannot Add More Quiz - Quiz Already Submitted"));
      } else{
        dispatch(showToast("Failed to Add"));
      }
      } else{
        dispatch(showToast("Some error occurred while adding quiz"));
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
    } finally {
      setTimeout(() => dispatch(hideToast()), 3000);
    }
  };

  const redirectToQuizFolder = () => {
    navigate("/quiz");
  };

  const buttons = [
    {
      label: "Back to Quiz Folder",
      theme: "danger",
      icon: <ArrowLeftSquareFill />,
      onClick: redirectToQuizFolder,
    },
  ];

  const handleCrop = (croppedImage: string) => {
    if (currentImageIndex !== null) {
      const newFilePreviews = [...filePreviews];
      newFilePreviews[currentImageIndex] = croppedImage;
      setFilePreviews(newFilePreviews);
    } else {
      setQuestionImagePreview(croppedImage);
    }
    setShowCropper(false);
  };

  return (
    <LayoutContainer title="Quiz">
      <div className="page-header d-flex align-items-center justify-content-left dashboard-container h-100">
        <h2>
          <i className="page-header-icon fa fa-home"></i>
          Add Quiz
        </h2>
      </div>
      <PageHeader buttons={buttons} />
      <div className="form-control-margin">
        <Form onSubmit={handleSubmit}>
          <Row>
          <Col xs={12}>
      {/* Dropdown for selecting between Quiz Folder and Training Quiz Folder */}
      <Form.Group controlId="formQuizFolderType" className="mb-3">
        <Form.Label>Select Folder Type <span className="text-danger">*</span></Form.Label>
        <InputGroup>
          <Form.Control
             as="select"
            className="required-field form-select"
            value={quizFolderType}
            onChange={(e) => {
              setQuizFolderType(e.target.value);
              setFolderId('');
            }}
          >
            <option value="Quiz Folder">Quiz Folder</option>
            <option value="Training Quiz Folder">Training Quiz Folder</option>
          </Form.Control>
        </InputGroup>
      </Form.Group>
    </Col>
            <Col xs={12}>
              <Form.Group controlId="formFolder" className="mb-3">
                <Form.Label>
                  Select Folder <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    as="select"
                    className="form-select required-field"
                    value={folderId}
                    required={true}
                    onChange={(e) => {
                      console.log(e);
                      setFolderId(e.target.value);
                    }}
                  >
                    <option value="" disabled hidden>Please select folder</option>

                    {/* Quiz Group
                    <optgroup label="Quiz">
                      {folders
                        .filter((folder) => folder.isTrainingQuiz === 0)
                        .map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.folder_name}
                          </option>
                        ))}
                    </optgroup>

                     Training Quiz Group 
                    <optgroup label="Training Quiz">
                      {folders
                        .filter((folder) => folder.isTrainingQuiz === 1)
                        .map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.folder_name}
                          </option>
                        ))}
                    </optgroup> */}
                     {quizFolderType === 'Quiz Folder' ? (
              <optgroup label="Quiz">
                {folders
                  .filter((folder) => folder.isTrainingQuiz === 0)
                  .map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.folder_name}
                    </option>
                  ))}
              </optgroup>
            ) : (
              <optgroup label="Training Quiz">
                {folders
                  .filter((folder) => folder.isTrainingQuiz === 1)
                  .map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.folder_name}
                    </option>
                  ))}
              </optgroup>
            )}
                  </Form.Control>

                  {/* <InputGroup.Text>
                    <CaretDownFill />
                  </InputGroup.Text> */}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formFieldType" className="mb-3">
              <Form.Label>Select Quiz Type <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <Form.Control
                    as="select"
                    className="form-select required-field"
                    value={selectedFieldType}
                    onChange={handleSelectedField}
                  >
                    <option>Textual Option</option>
                    <option>Descriptive Option</option>
                    <option>Image Ordering Option</option>
                    <option>Image Option</option>
                  </Form.Control>
                  {/* <InputGroup.Text>
                    <CaretDownFill />
                  </InputGroup.Text> */}
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group controlId="formQuestion" className="mb-3 custom-width">
            <Form.Label>
              Enter Question <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter question"
              className="form-question"
              onChange={handleQuestionChange}
              required
              maxLength={500}
            />
          </Form.Group>

          <Form.Group
            controlId="formPoints"
            className="mb-3 custom-width-points"
          >
            <Form.Label>
              Enter Points <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter Points"
              className="form-points no-arrows"
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value > 0) {
                  setPoints(value);
                } else {
                  dispatch(showToast("Quiz Score can't be 0"));
                  setTimeout(() => dispatch(hideToast()), 3000);
                }
              }}
              min={1}
              max={99}
              required
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
          </Form.Group>

          <Form.Group controlId="formQuestionImage" className="mb-3">
            <Row>
              <Col xs={9}>
                <Form.Label>Question Image</Form.Label>
                <Form.Control
  type="file"
  accept=".jpg,.jpeg,.png"
  onChange={handleQuestionImageChange}
/>

                <p className="font-14 mt-3 text-danger fw-bold">
                  Uploads must be JPG / JPEG / PNG
                </p>
              </Col>
              {questionImagePreview && (
                <Col xs={3} className="position-relative">
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <img
                      src={questionImagePreview}
                      alt="Selected"
                      style={{
                        maxWidth: "100px",
                        maxHeight: "100px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                      }}
                    />
                    {/* <XCircleFill
                      size={20}
                      className="text-danger"
                      style={{
                        position: "absolute",
                        top: "2px",
                        right: "2px",
                        cursor: "pointer",
                        zIndex: 1,
                      }}
                      onClick={() => setQuestionImagePreview(null)}
                    /> */}
                  </div>
                </Col>
              )}
            </Row>
          </Form.Group>

          {selectedFieldType === "Textual Option" &&
            options.map((option, index) => (
              <Form.Group
                key={index}
                controlId={`formTextOption-${index}`}
                className="mb-3"
              >
                <Row className="align-items-center">
                  <Col xs={1}>
                    <Form.Check
                      type="radio"
                      name="correctAnswer"
                      checked={selectedOption === index}
                      onChange={(e:React.ChangeEvent<HTMLInputElement>) =>{ 
                        handleOptionSelect(index)
                        e.currentTarget.blur()
                      }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Form.Control
                      type="text"
                      value={option}
                      placeholder="Quiz Option"
                      // maxLength={100}
                      onChange={(e) =>
                        handleOptionChange(index, e.target.value)
                      }
                      className="required-field"
                    />
                    {optionErrors[index] && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {optionErrors[index]}
                      </div>
                    )}
                  </Col>
                  <Col xs={2}>
                    {index > 0 ? (
                      <Button
                        variant="secondary"
                        onClick={() => handleRemoveOption(index)}
                      >
                        × Remove
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        onClick={handleAddOption}
                        className="btn-add"
                        disabled={options.some((option) => option === "")}
                      >
                        + Add More
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form.Group>
            ))}

          {selectedFieldType === "Descriptive Option"}

          {selectedFieldType === "Image Option" &&
            options.map((option, index) => (
              <Form.Group
                key={index}
                controlId={`formImageOption-${index}`}
                className="mb-3"
              >
                <Row className="align-items-start">
                  <Col
                    className="d-flex justify-content-center align-items-center"
                    xs={1}
                  >
                    <Form.Check
                      type="radio"
                      name="correctAnswer"
                      checked={selectedOption === index}
                      onChange={(e:React.ChangeEvent<HTMLInputElement>) => {
                        handleOptionSelect(index)
                        e.currentTarget.blur()
                      }}
                    />
                  </Col>
                  <Col xs={3}>
                    <Form.Control
                      as="input"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"

                      ref={fileInputRef}
                      onChange={(e) =>
                        handleImageChange(
                          index,
                          e as React.ChangeEvent<HTMLInputElement>
                        )
                      }
                    />
                    <Form.Text className="text-muted">
                      Format Support jpg, jpeg, png
                    </Form.Text>
                  </Col>
                  {filePreviews[index] && (
                    <Col xs={3} className="position-relative">
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <img
                          src={filePreviews[index]!}
                          alt="Selected"
                          style={{
                            maxWidth: "100px",
                            maxHeight: "100px",
                            objectFit: "cover",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                          }}
                        />
                        {/* <XCircleFill
                          size={20}
                          className="text-danger"
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            cursor: "pointer",
                            zIndex: 1,
                          }}
                          onClick={() => handleImageRemove(index)}
                        /> */}
                      </div>
                    </Col>
                  )}
                  <Col xs={2}>
                    {index > 0 ? (
                      <Button
                        variant="secondary"
                        onClick={() => handleRemoveOption(index)}
                      >
                        × Remove
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        onClick={handleAddOption}
                        className="ml-2"
                        disabled={options.some(
                          (option, index) => !optionFiles[index]
                        )}
                      >
                        + Add More
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form.Group>
            ))}

          {selectedFieldType === "Image Ordering Option" &&
            options.map((option, index) => (
              <Form.Group
                key={index}
                controlId={`formImageOrder-${index}`}
                className="mb-3"
              >
                <Row className="align-items-start">
                  <Col xs={2}>
                    <Form.Control
                      type="number"
                      value={option}
                      placeholder="Order"
                      onChange={(e) =>
                        handleOptionChange(index, e.target.value)
                        
                      }
                      className="required-field"
                      min={1}
                      max={99}
                      required
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    />
                    {optionErrors[index] && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {optionErrors[index]}
                      </div>
                    )}
                  </Col>
                  <Col xs={3}>
                    <Form.Control
                      as="input"
                       accept="image/jpeg,image/jpg,image/png"
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) =>
                        handleImageChange(
                          index,
                          e as React.ChangeEvent<HTMLInputElement>
                        )
                      }
                    />
                    <Form.Text className="text-muted">
                      Format Support jpg, jpeg, png
                    </Form.Text>
                  </Col>
                  {filePreviews[index] && (
                    <Col xs={3} className="position-relative">
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <img
                          src={filePreviews[index]!}
                          alt="Selected"
                          style={{
                            maxWidth: "100px",
                            maxHeight: "100px",
                            objectFit: "cover",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                          }}
                        />
                        {/* <XCircleFill
                          size={20}
                          className="text-danger"
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            cursor: "pointer",
                            zIndex: 1,
                          }}
                          onClick={() => handleImageRemove(index)}
                        /> */}
                      </div>
                    </Col>
                  )}
                  <Col xs={2}>
                    {index > 0 ? (
                      <Button
                        variant="secondary"
                        onClick={() => handleRemoveOption(index)}
                      >
                        × Remove
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        onClick={handleAddOption}
                        className=""
                        disabled={options.some(
                          (option, index) =>
                            option === "" || !optionFiles[index]
                        )}
                      >
                        + Add More
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form.Group>
            ))}

          {/* {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage.split("\n").map((msg, idx) => (
                <div key={idx}>{msg}</div>
              ))}
            </div>
          )} */}

          <div className="d-flex justify-content-center">
            <Button type="submit" className="btn-submit text-white" onClick={(e:React.MouseEvent<HTMLButtonElement>)=> e.currentTarget.blur()}>
              Submit
            </Button>
          </div>
        </Form>
      </div>

      <CroppingModal
        show={showCropper}
        handleClose={() => setShowCropper(false)}
        image={currentImage || ""}
        onCrop={handleCrop}
      />
    </LayoutContainer>
  );
};

export default AddQuiz;